/**
 * CRUD Service 工厂
 *
 * 基于 @umijs/max 的 request 函数，为业务实体生成标准的
 * list / detail / create / update / delete / toggleStatus 请求方法，
 * 消除大量手写重复 service 的问题。
 */

import type { ApiResponse, PaginatedData } from '../types/api';

// ===== 类型定义 =====

/**
 * 请求方法
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * 单个操作的自定义配置
 */
export interface OperationConfig {
  /** HTTP 方法 */
  method?: HttpMethod;
  /** 路径后缀 (支持 :id 占位符) */
  path?: string;
  /** 额外请求配置 (如 skipEncrypt) */
  requestOptions?: Record<string, unknown>;
}

/**
 * CRUD Service 工厂配置
 */
export interface CrudServiceConfig {
  /** 分页列表 — 默认 POST /page + { pageNo, pageSize, ... } */
  list?: OperationConfig | false;
  /** 查询详情 — 默认 GET /:id */
  detail?: OperationConfig | false;
  /** 创建 — 默认 POST / */
  create?: OperationConfig | false;
  /** 更新 — 默认 PUT / */
  update?: OperationConfig | false;
  /** 删除 — 默认 DELETE /:id */
  delete?: OperationConfig | false;
  /** 切换状态 — 默认 PUT /:id/status */
  toggleStatus?: OperationConfig | false;
  /** id 字段名，默认 'id' */
  idField?: string;
  /**
   * request 函数，来自 @umijs/max 的 request。
   * 不在此包中直接 import，由调用方注入以保持零依赖。
   */
  request: <R>(url: string, options?: Record<string, unknown>) => Promise<R>;
}

/**
 * CRUD Service 返回类型 — 按配置动态生成
 */
export interface CrudServiceMethods<
  T = Record<string, unknown>,
  CreateDTO = Partial<T>,
  UpdateDTO = Partial<T>,
  ListParams = Record<string, unknown>,
> {
  /** 分页列表 */
  list: (params: ListParams) => Promise<ApiResponse<PaginatedData<T>>>;
  /** 查询详情 */
  detail: (id: string | number) => Promise<ApiResponse<T>>;
  /** 创建 */
  create: (data: CreateDTO) => Promise<ApiResponse<T>>;
  /** 更新 */
  update: (data: UpdateDTO) => Promise<ApiResponse<T>>;
  /** 删除 */
  remove: (id: string | number) => Promise<ApiResponse<unknown>>;
  /** 切换状态 */
  toggleStatus: (
    id: string | number,
    status: string | number | boolean,
  ) => Promise<ApiResponse<unknown>>;
  /** 保存 (create if no id, update if has id; 可通过 isNew 显式指定) */
  save: (
    data: CreateDTO | UpdateDTO,
    isNew?: boolean,
  ) => Promise<ApiResponse<T>>;
}

// ===== 内部工具 =====

/**
 * 替换路径中的 :id 占位符
 */
function resolvePath(
  base: string,
  pathSuffix: string,
  id?: string | number,
): string {
  const fullPath = pathSuffix ? `${base}${pathSuffix}` : base;
  if (id !== undefined) {
    return fullPath.replace(':id', String(id));
  }
  return fullPath;
}

/**
 * 根据 HTTP 方法决定参数放在 params 还是 data
 */
function buildRequestOptions(
  method: HttpMethod,
  payload?: unknown,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  const options: Record<string, unknown> = { method, ...extra };
  if (payload === undefined || payload === null) return options;

  if (method === 'GET' || method === 'DELETE') {
    options.params = payload;
  } else {
    options.data = payload;
  }
  return options;
}

// ===== 默认配置 =====

const DEFAULT_OPS: Record<string, { method: HttpMethod; path: string }> = {
  list: { method: 'POST', path: '/page' },
  detail: { method: 'GET', path: '/:id' },
  create: { method: 'POST', path: '' },
  update: { method: 'PUT', path: '' },
  delete: { method: 'DELETE', path: '/:id' },
  toggleStatus: { method: 'PUT', path: '/:id/status' },
};

// ===== 工厂函数 =====

/**
 * 创建 CRUD Service
 *
 * @param basePath - API 基础路径，如 '/kc-api/users'
 * @param config - 配置项
 * @returns CRUD 方法集合
 *
 * @example
 * ```ts
 * import { request } from '@umijs/max';
 *
 * const userService = createCrudService<User>('/kc-api/users', {
 *   request,
 *   list: { method: 'POST', path: '/page' },
 *   detail: { method: 'GET', path: '/:id' },
 *   create: { method: 'POST' },
 *   update: { method: 'PUT' },
 *   delete: { method: 'DELETE', path: '/:id' },
 * });
 *
 * // 使用
 * const res = await userService.list({ pageNo: 1, pageSize: 10 });
 * const user = await userService.detail(1);
 * await userService.create({ name: 'test' });
 * await userService.save({ id: 1, name: 'updated' }); // 自动选择 update
 * await userService.remove(1);
 * ```
 *
 * @example
 * ```ts
 * // 禁用不需要的操作
 * const readonlyService = createCrudService<Article>('/api/articles', {
 *   request,
 *   create: false,
 *   update: false,
 *   delete: false,
 *   toggleStatus: false,
 * });
 * ```
 */
export function createCrudService<
  T = Record<string, unknown>,
  CreateDTO = Partial<T>,
  UpdateDTO = Partial<T>,
  ListParams = Record<string, unknown>,
>(
  basePath: string,
  config: CrudServiceConfig,
): CrudServiceMethods<T, CreateDTO, UpdateDTO, ListParams> {
  const { request: requestFn, idField = 'id' } = config;

  // 获取某个操作的配置 (合并默认 + 自定义)
  function getOpConfig(opName: string): {
    method: HttpMethod;
    path: string;
    requestOptions?: Record<string, unknown>;
  } | null {
    const userCfg = config[opName as keyof typeof config];
    if (userCfg === false) return null;

    const defaultCfg = DEFAULT_OPS[opName];
    if (!defaultCfg) return null;

    if (
      typeof userCfg === 'object' &&
      userCfg !== null &&
      !Array.isArray(userCfg)
    ) {
      const opCfg = userCfg as OperationConfig;
      return {
        method: opCfg.method ?? defaultCfg.method,
        path: opCfg.path ?? defaultCfg.path,
        requestOptions: opCfg.requestOptions,
      };
    }

    return { ...defaultCfg };
  }

  // --- list ---
  const listOp = getOpConfig('list');
  const list = async (params: ListParams) => {
    if (!listOp) throw new Error('list operation is disabled');
    const url = resolvePath(basePath, listOp.path);
    return requestFn<ApiResponse<PaginatedData<T>>>(
      url,
      buildRequestOptions(listOp.method, params, listOp.requestOptions),
    );
  };

  // --- detail ---
  const detailOp = getOpConfig('detail');
  const detail = async (id: string | number) => {
    if (!detailOp) throw new Error('detail operation is disabled');
    const url = resolvePath(basePath, detailOp.path, id);
    return requestFn<ApiResponse<T>>(
      url,
      buildRequestOptions(detailOp.method, undefined, detailOp.requestOptions),
    );
  };

  // --- create ---
  const createOp = getOpConfig('create');
  const create = async (data: CreateDTO) => {
    if (!createOp) throw new Error('create operation is disabled');
    const url = resolvePath(basePath, createOp.path);
    return requestFn<ApiResponse<T>>(
      url,
      buildRequestOptions(createOp.method, data, createOp.requestOptions),
    );
  };

  // --- update ---
  const updateOp = getOpConfig('update');
  const update = async (data: UpdateDTO) => {
    if (!updateOp) throw new Error('update operation is disabled');

    let payload: unknown = data;
    let id: string | number | undefined;

    // 当路径包含 :id 时，从 data 中提取 id 并从 payload 中移除
    if (updateOp.path.includes(':id')) {
      const record = data as Record<string, unknown>;
      id = record[idField] as string | number;
      const { [idField]: _, ...rest } = record;
      payload = rest;
    }

    const url = resolvePath(basePath, updateOp.path, id);
    return requestFn<ApiResponse<T>>(
      url,
      buildRequestOptions(updateOp.method, payload, updateOp.requestOptions),
    );
  };

  // --- delete ---
  const deleteOp = getOpConfig('delete');
  const remove = async (id: string | number) => {
    if (!deleteOp) throw new Error('delete operation is disabled');
    const url = resolvePath(basePath, deleteOp.path, id);
    return requestFn<ApiResponse<unknown>>(
      url,
      buildRequestOptions(deleteOp.method, undefined, deleteOp.requestOptions),
    );
  };

  // --- toggleStatus ---
  const toggleStatusOp = getOpConfig('toggleStatus');
  const toggleStatus = async (
    id: string | number,
    status: string | number | boolean,
  ) => {
    if (!toggleStatusOp) throw new Error('toggleStatus operation is disabled');
    const url = resolvePath(basePath, toggleStatusOp.path, id);
    return requestFn<ApiResponse<unknown>>(
      url,
      buildRequestOptions(
        toggleStatusOp.method,
        { status },
        toggleStatusOp.requestOptions,
      ),
    );
  };

  // --- save (auto create/update) ---
  const save = async (data: CreateDTO | UpdateDTO, isNew?: boolean) => {
    // 显式指定时直接使用，无需推断
    if (isNew === true) return create(data as CreateDTO);
    if (isNew === false) return update(data as UpdateDTO);

    // 自动推断：检查 idField 是否有有效值（注意 0 是合法 id）
    const record = data as Record<string, unknown>;
    const idValue = record[idField];
    const hasId = idValue !== undefined && idValue !== null && idValue !== '';
    if (hasId) {
      return update(data as UpdateDTO);
    }
    return create(data as CreateDTO);
  };

  return {
    list,
    detail,
    create,
    update,
    remove,
    toggleStatus,
    save,
  };
}

/**
 * 快速创建标准 CRUD Service 的简化版本
 *
 * 适用于完全遵循标准 RESTful 约定的场景。
 *
 * @example
 * ```ts
 * const userService = createStandardCrudService<User>(request, '/kc-api/users');
 * const articleService = createStandardCrudService<Article>(request, '/api/articles', {
 *   list: { path: '/page' },
 * });
 * ```
 */
export function createStandardCrudService<T = Record<string, unknown>>(
  requestFn: <R>(url: string, options?: Record<string, unknown>) => Promise<R>,
  basePath: string,
  overrides?: Partial<
    Pick<
      CrudServiceConfig,
      | 'list'
      | 'detail'
      | 'create'
      | 'update'
      | 'delete'
      | 'toggleStatus'
      | 'idField'
    >
  >,
): CrudServiceMethods<T, Partial<T>, Partial<T>, Record<string, unknown>> {
  return createCrudService<T>(basePath, {
    request: requestFn,
    ...overrides,
  });
}
